import React, { memo, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Paper,
  Tooltip,
  Typography,
  Grid,
  Stack
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ReportIcon from '@mui/icons-material/Report';
import { FormattedMessage } from 'react-intl';
import { Connector } from 'types/types';

type useStylesProp = {
  isInstalled: boolean;
  hasInstallError: boolean;
};

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: theme.spacing(27),
    borderRadius: theme.spacing(1),
    margin: theme.spacing(2, 1, 0, 1),
    width: theme.spacing(31.125),
    borderColor: ({ hasInstallError }: useStylesProp) =>
      hasInstallError ? theme.palette.error.dark : 'rgba(0,0,0,0.12)',
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      boxShadow: '0 0 5px rgba(33,33,33,.3)'
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 5px rgba(33,33,33,.3)'
    },
    paddingBottom: theme.spacing(1),
    cursor: 'pointer'
  },
  media: {
    borderRadius: theme.spacing(0),
    height: theme.spacing(15),
    display: 'flex',
    alignItems: 'flex-end',
    flexDirection: 'column'
  },
  connectorIcon: {
    maxHeight: theme.spacing(15),
    flexGrow: 1
  },
  title: {
    fontFamily: theme.typography.fontFamily,
    lineHeight: theme.spacing(2.5),
    overflow: 'visible',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    position: 'relative'
  },
  connected: {
    padding: theme.spacing(0, 0.8),
    ...theme.typography.caption,
    color: theme.palette.success.light,
    backgroundColor: theme.palette.success.dark,
    marginLeft: theme.spacing(1.5),
    float: 'left',
    position: 'relative',
    bottom: theme.spacing(1.2),
    top: theme.spacing(-1.2),
    width: 'fit-content',
    borderRadius: theme.spacing(0.5)
  },
  cardContent: {
    padding: ({ isInstalled }: useStylesProp) =>
      isInstalled
        ? `${theme.spacing(0, 2, 0, 2)} !important`
        : `${theme.spacing(2.2, 2, 0, 2)} !important`,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(1),
    justifyContent: 'space-between'
  },
  category: {
    display: 'flex',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize
  },
  categoryBox: {
    position: 'relative',
    margin: theme.spacing(-2.75, 0, 1, 0)
  },
  stack: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    position: 'absolute'
  },
  reportIcon: {
    backgroundColor: theme.palette.error.main,
    color: 'white',
    height: theme.spacing(3.75),
    width: theme.spacing(3.75)
  },
  errorText: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.error.main,
    color: 'white',
    height: theme.spacing(3.75),
    width: theme.spacing(10)
  }
}));

const useTooltipClasses = makeStyles((theme) => ({
  popper: {
    marginBottom: `${theme.spacing(-13)} !important`
  }
}));

type ConnectorCardComponentProps = {
  info: Connector;
  index: number;
  handleOpenDialog: (item: Connector) => void;
};

export const ConnectorCardComponent: React.FC<ConnectorCardComponentProps> = memo(
  ({ info, index, handleOpenDialog }: ConnectorCardComponentProps) => {
    const tooltip = useTooltipClasses();

    const classes = useStyles({
      isInstalled: Boolean(info.installed),
      hasInstallError: false
    });

    const getInstallStatus = useCallback(
      (item: Connector) => {
        if (item.installed)
          return (
            <div>
              <span
                className={classes.connected}
                data-testid={'connected-button-' + item.name}
              >
                <FormattedMessage id="connectors.installed" />
              </span>
            </div>
          );
      },
      [classes.connected]
    );

    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === 'Enter') {
          handleOpenDialog(info);
        }
      },
      [handleOpenDialog, info]
    );
    const isError: boolean = Object.values(info?.isDagsError || []).some(
      (value: boolean) => value === true
    );

    return (
      <Grid item key={info.name} data-testid={`connectoritem${info.name}`}>
        <Card
          className={classes.card}
          elevation={0}
          variant="outlined"
          key={info.name}
          onClick={() => handleOpenDialog(info)}
          data-testid={`connectorcard${info.name}`}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <CardMedia>
            <Paper className={classes.media} elevation={0}>
              {isError && (
                <Stack className={classes.stack}>
                  <ReportIcon fontSize="small" className={classes.reportIcon} />
                  <Typography variant="body2" className={classes.errorText}>
                    <FormattedMessage id={'connectorError.errorMessage1'} />
                  </Typography>
                </Stack>
              )}
              <img
                className={classes.connectorIcon}
                src={info.image.src}
                alt={`connectorImage${index}`}
                data-testid={`connectorImage${index}`}
              />
            </Paper>
          </CardMedia>
          {getInstallStatus(info)}
          <CardContent className={classes.cardContent}>
            <Tooltip
              title={info.name}
              data-testid={`connector-title-tooltip${index}`}
              placement="top-start"
              classes={tooltip}
            >
              <Typography
                className={classes.title}
                variant="subtitle1"
                data-testid={`connectorName${index}`}
              >
                {info.name}
              </Typography>
            </Tooltip>
            <div
              data-testid={`connector-category-content${index}`}
              className={classes.categoryBox}
            >
              <Typography
                className={classes.category}
                data-testid="category-content"
              >
                {info.category}
              </Typography>
            </div>
          </CardContent>
        </Card>
      </Grid>
    );
  }
);
ConnectorCardComponent.displayName = 'ConnectorCardComponent';
